import cn from 'classnames';
import { useQuery } from '@apollo/client';
// Api
import { GET_TICKET_AVAILABILITY } from 'api/ticketEvents/queries';
// Types
import { PublicTicketEventFields } from 'api/ticketEvents/types/PublicTicketEventFields';
import {
  GetTicketAvailability,
  GetTicketAvailabilityVariables,
} from 'api/ticketEvents/types/GetTicketAvailability';
// Helpers
import { formatCurrencyStringWithoutName } from 'helpers/formatCurrencyString';
import {
  areAllTiersSoldOut,
  formatTicketEventShortDate,
  getTicketSalesStoppedText,
  isTicketEventSelling,
} from 'helpers/ticketSales';
// Hooks
import { useTicketingLaunched } from 'hooks/useTicketingLaunched';
// Components
import GetTicketsButton from '../GetTicketsButton/GetTicketsButton';
import HeldTicketsLink from '../HeldTicketsLink/HeldTicketsLink';
// Styles
import styles from './TicketPriceBox.module.scss';

type TicketPriceBoxProps = {
  event: PublicTicketEventFields;
  className?: string;
};

const TicketPriceBox = ({
  event,
  className,
}: TicketPriceBoxProps): JSX.Element | null => {
  const ticketingLaunched = useTicketingLaunched();
  const selling = ticketingLaunched && isTicketEventSelling(event);
  // The event itself carries no stock, so the tiers tell whether anything is left.
  const { data: availabilityData } = useQuery<
    GetTicketAvailability,
    GetTicketAvailabilityVariables
  >(GET_TICKET_AVAILABILITY, {
    variables: { input: { ticketEventId: event.id } },
    skip: !selling,
    fetchPolicy: 'cache-and-network',
  });
  const soldOut = areAllTiersSoldOut(
    availabilityData?.ticketAvailability ?? []
  );

  // Published but selling nothing: there is no price or sales state to show.
  if (!ticketingLaunched || !event.ticketsEnabled) {
    return null;
  }

  const renderStatus = () => {
    if (!selling) {
      return <p className={styles.price}>{getTicketSalesStoppedText(event)}</p>;
    }

    if (soldOut) {
      return <p className={styles.price}>Sold out</p>;
    }

    return event.ticketPriceFrom != null ? (
      <p className={styles.price}>
        {`From ${formatCurrencyStringWithoutName(event.ticketPriceFrom)}`}
      </p>
    ) : null;
  };

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.details}>
        {renderStatus()}
        <p className={styles.date}>
          {formatTicketEventShortDate(event.startsAt, event.timezone)}
        </p>
        <HeldTicketsLink ticketEventId={event.id} />
      </div>
      {selling && <GetTicketsButton event={event} soldOut={soldOut} bordered />}
    </div>
  );
};

export default TicketPriceBox;
